import React, { useEffect, useRef } from 'react';
import styles from './ProductGrid.module.css';
import ProductCard from './ProductCard';

// Reuse ProductCard CSS classes so skeletons match EXACT dimensions (imgBox heights, paddings, etc.)
import cardStyles from './ProductCard.module.css';

import SkeletonLoading from '../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';

export default function ProductGrid({ items, total, loaded, onHitBottom, sortBy, onSortBy, isLoading = false }) {
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (isLoading) return;
        const el = sentinelRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) onHitBottom?.();
                });
            },
            { rootMargin: '400px 0px 0px 0px' }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [onHitBottom, items.length, isLoading]);

    // How many skeletons to render while boot-loading
    const SKELETON_COUNT = 9; // 3x3 grid by default (responsive will adjust)

    return (
        <div className={styles.wrap} aria-busy={isLoading}>
            <div className={styles.header} >
                <h3>Products</h3>
                <div className={styles.right}>
                    <span className={styles.total}>{total} items</span>
                    <div className={styles.sortBox}>
                        <label className={styles.sortLabel}>Sort by:</label>
                        <select
                            value={sortBy}
                            onChange={(e) => onSortBy(e.target.value)}
                            className={styles.select}
                            disabled={isLoading}
                        >
                            <option value="newest">Newest</option>
                            <option value="price_asc">Price: Low → High</option>
                            <option value="price_desc">Price: High → Low</option>
                            <option value="discount">Highest Discount</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className={styles.grid}>
                {isLoading
                    ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                        <div key={`sk-${i}`} className={cardStyles.card}>
                            {/* image skeleton */}
                            <div className={cardStyles.imgBox}>
                                <SkeletonLoading length={1} height="100%" width="100%" br="15px" padding="0" />
                            </div>

                            {/* body skeleton */}
                            <div className={cardStyles.body}>
                                {/* title row: title + status dot */}
                                <div className={cardStyles.titleRow} style={{ alignItems: 'center' }}>
                                    <SkeletonLoading length={1} height="14px" width="60%" br="6px" padding="0 6px 0 0" />
                                </div>

                                {/* subtitle */}
                                <SkeletonLoading length={1} height="13px" width="80%" br="6px" padding="6px 6px 0 0" />

                                {/* specs (4 lines) */}
                                <ul className={cardStyles.specs} style={{ listStyle: 'none', padding: '6px 0 0 0' }}>
                                    <SkeletonLoading length={4} height="13px" width="70%" br="6px" padding="2px 0" />
                                </ul>
                            </div>

                            {/* footer skeleton */}
                            <div className={cardStyles.footer}>
                                <SkeletonLoading length={1} height="16px" width="22%" br="8px" padding="0" />
                                <SkeletonLoading length={1} height="28px" width="96px" br="10px" padding="0" />
                            </div>
                        </div>
                    ))
                    : items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>

            <div className={styles.footerInfo}>
                <span>Showing {Math.min(loaded, total)} of {total}</span>
            </div>

            {/* sentinel for infinite scroll (skip while loading) */}
            {!isLoading && <div ref={sentinelRef} className={styles.sentinel} />}
        </div>
    );
}
